import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { EntDescription } from '@bazis/shared/models/srv.types';

/**
 * Breadcrumb item used to build navigation path in UI.
 *
 * `entity` can describe how to resolve a dynamic breadcrumb name from an entity.
 * `parents` is a list of parent breadcrumb items for custom breadcrumb structures.
 */
export type BreadcrumbItem = {
    /** Optional arbitrary data attached to the breadcrumb. */
    data?: any;
    /** Translation key or plain text for breadcrumb label. */
    name: string;
    /** Optional type discriminator used by consumers. */
    type?: string;
    /**
     * Entity-based breadcrumb configuration.
     * Use when breadcrumb name depends on an entity loaded by type and id.
     */
    entity?: {
        /** Entity type string used by entity service (example: `document.document`). */
        entityType: string;
        /** Route param name where entity id is stored (example: `id`). */
        idParamName?: string;
        /** Snapshot field name used as breadcrumb label (example: `title`). */
        nameField?: string;
    };
    /** Absolute URL (router path) for this breadcrumb item. */
    url: string;
    /** Route params captured for this breadcrumb level. */
    params?: { [index: string]: any };
    /** Query params that should be preserved for this breadcrumb level. */
    queryParams?: { [index: string]: any };
    /** Explicit list of parent breadcrumbs (used for custom parent chains). */
    parents: BreadcrumbItem[];
    /** Optional icon name for displaying breadcrumb as icon. */
    icon?: string;
    /** If true, UI can render only icon without text. */
    useOnlyIcon?: boolean;
};

/**
 * Root breadcrumb item used as the first element of the breadcrumbs list.
 */
const ROOT = { name: 'breadcrumbRootTitle', url: '/', parents: [] };

/**
 * **BazisBreadcrumbsService**
 *
 * Builds and stores breadcrumbs for the current router state.
 * Uses route configuration (`data.breadcrumb`) and route params to generate
 * a list of `BreadcrumbItem` objects for UI components.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisBreadcrumbsService {
    /**
     * Current breadcrumbs list.
     * Consumers subscribe to render breadcrumb navigation.
     */
    breadcrumbs$: BehaviorSubject<BreadcrumbItem[]> = new BehaviorSubject([]);

    /**
     * Stores query params for routes where `keepGetParams` flag is enabled.
     * Key is a built URL, value is a snapshot of query params.
     */
    protected _savedParams = {};

    /**
     * Recursively creates breadcrumbs from an `ActivatedRoute` tree.
     *
     * The method walks through `route.children`, builds URL segments,
     * and reads breadcrumb config from `child.snapshot.data.breadcrumb`.
     *
     * When there are no more children, it publishes the built list to `breadcrumbs$`.
     *
     * @param route - Root `ActivatedRoute` to start from (usually `ActivatedRoute` from component).
     * @param role - Optional role identifier used with `data.roleBreadcrumb` overrides. Default: `null`.
     * @param url - URL prefix accumulated during recursion. Default: empty string.
     * @param breadcrumbs - Current breadcrumbs list. Default: `[ROOT]`.
     * @returns void
     */
    public createBreadcrumbs(
        route: ActivatedRoute,
        role = null,
        url: string = '',
        breadcrumbs: BreadcrumbItem[] = [ROOT],
    ) {
        const children: ActivatedRoute[] = route.children;
        if (children.length === 0) {
            this.breadcrumbs$.next(breadcrumbs);
            return;
        }

        children.forEach((child: ActivatedRoute, index) => {
            const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');
            if (routeURL !== '') {
                url += `/${routeURL}`;
            }

            if (child.snapshot.data.breadcrumb) {
                if (breadcrumbs.length > 0 && url === breadcrumbs[breadcrumbs.length - 1].url) {
                    const currentLast = breadcrumbs[breadcrumbs.length - 1];
                    let parentsCount = (currentLast.parents ? currentLast.parents.length : 0) + 1;
                    breadcrumbs.splice(breadcrumbs.length - parentsCount, parentsCount);
                }

                breadcrumbs = this.generateBreadcrumbItem(
                    child,
                    role,
                    url,
                    breadcrumbs,
                    index === children.length - 1,
                    child.snapshot.data.breadcrumb.icon,
                    child.snapshot.data.breadcrumb.useOnlyIcon,
                );
            }

            return this.createBreadcrumbs(child, role, url, breadcrumbs);
        });
    }

    /**
     * Build a breadcrumb item for one route level and add/replace it in `breadcrumbs`.
     *
     * Uses `data.roleBreadcrumb[role]` when present, otherwise `data.breadcrumb`.
     * Supports:
     * - `keepGetParams`: preserve query params for generated URL
     * - `nameTpl`: template string that can reference route params
     * - `parents`: custom parent breadcrumb chain
     *
     * @param child - Current route node.
     * @param role - Role identifier used to pick role-specific breadcrumb config.
     * @param url - Full accumulated URL for this route node.
     * @param breadcrumbs - Current breadcrumbs list to mutate.
     * @param isLast - If true, this node is the last child in current level.
     * @param icon - Optional icon name to attach to breadcrumb.
     * @param useOnlyIcon - Optional flag to render breadcrumb as icon only.
     * @returns Updated breadcrumbs list.
     */
    generateBreadcrumbItem(child, role, url, breadcrumbs, isLast, icon?, useOnlyIcon?) {
        const data =
            role && child.snapshot.data.roleBreadcrumb && child.snapshot.data.roleBreadcrumb[role]
                ? child.snapshot.data.roleBreadcrumb[role]
                : child.snapshot.data.breadcrumb;

        if (isLast && data.keepGetParams) this._savedParams[url] = child.snapshot.queryParams;

        let name = data.name;
        const params = child.snapshot.params;

        breadcrumbs = this.generateCustomParentBreadcrumbs(data, breadcrumbs, params);

        if (!name && data.nameTpl) {
            name = data.nameTpl.replace(/\${([A-Za-z0-9_#-]+)}/g, (_, v) => {
                const parts = v.split('#');
                if (parts[0] === 'param') {
                    return params[parts[1]];
                }
                return v;
            });
        }

        const breadcrumb = {
            ...data,
            name,
            url,
            params,
            queryParams: this._savedParams[url] || {},
            icon,
            useOnlyIcon,
        };

        breadcrumbs = this.addToBreadcrumbs(breadcrumbs, breadcrumb);

        return breadcrumbs;
    }

    /**
     * Add a breadcrumb to the list or replace the last item when URL matches.
     *
     * This prevents duplicates when navigating within the same URL and
     * allows refreshing label/icon/query params.
     *
     * @param breadcrumbs - Current breadcrumbs array.
     * @param breadcrumb - Breadcrumb item to add or replace.
     * @returns Updated breadcrumbs array reference.
     */
    addToBreadcrumbs(breadcrumbs, breadcrumb) {
        if (!breadcrumbs.length || breadcrumb.url !== breadcrumbs[breadcrumbs.length - 1].url)
            breadcrumbs.push(breadcrumb);
        else breadcrumbs[breadcrumbs.length - 1] = breadcrumb;

        return breadcrumbs;
    }

    /**
     * Generate and insert custom parent breadcrumbs defined in route breadcrumb config.
     *
     * Each parent URL can include `:paramName` parts that are replaced with values from `params`.
     *
     * @param data - Breadcrumb config object (usually from `route.snapshot.data.breadcrumb`).
     * @param breadcrumbs - Current breadcrumbs list to mutate.
     * @param params - Route params used for URL interpolation.
     * @returns Updated breadcrumbs list.
     */
    generateCustomParentBreadcrumbs(data, breadcrumbs, params) {
        if (data.parents) {
            data.parents.forEach((parent) => {
                const url = parent.url
                    .split('/')
                    .map((part) => {
                        if (part[0] === ':' && params[part.substring(1)]) {
                            return params[part.substring(1)];
                        }
                        return part;
                    })
                    .join('/');
                const breadcrumb = {
                    ...parent,
                    url,
                    params,
                    queryParams: this._savedParams[url] || {},
                };
                breadcrumbs = this.addToBreadcrumbs(breadcrumbs, breadcrumb);
            });
        }
        return breadcrumbs;
    }
}
